import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { 
  getConnections, 
  getAuthorizationUrl, 
  disconnectConnection as disconnectConnectionApi 
} from '../services/oauthApi';
import { openOAuthPopup, isUserCancelledError, getOAuthErrorMessage } from '../utils/oauthPopup';
import { useAuth } from './AuthContext';

const OAuthContext = createContext();

export const useOAuth = () => {
  const context = useContext(OAuthContext);
  if (!context) {
    throw new Error('useOAuth must be used within an OAuthProvider');
  }
  return context;
};

export const OAuthProvider = ({ children }) => {
  const { getUserIdDiscover } = useAuth();
  const [connections, setConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all connections
  const fetchConnections = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const userId = getUserIdDiscover();
      const data = await getConnections(userId);
      setConnections(data);
    } catch (err) {
      console.error('Failed to fetch connections:', err);
      setError(err.message);
      setConnections([]);
    } finally {
      setIsLoading(false);
    }
  }, [getUserIdDiscover]);

  // Load connections on mount
  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // Initiate OAuth flow
  const initiateOAuth = useCallback(async (provider) => {
    try {
      setIsConnecting(true);
      setError(null);
      
      const userId = getUserIdDiscover();
      const authUrl = await getAuthorizationUrl(provider, userId);
      
      const result = await openOAuthPopup(authUrl);
      
      // Refresh connections after successful OAuth
      await fetchConnections();
      
      return { success: true, ...result };
    } catch (err) {
      if (isUserCancelledError(err)) {
        return { success: false, cancelled: true, message: 'Authorization was cancelled' };
      }
      
      const errorMessage = getOAuthErrorMessage(err);
      setError(errorMessage);
      return { success: false, cancelled: false, message: errorMessage };
    } finally {
      setIsConnecting(false);
    }
  }, [fetchConnections, getUserIdDiscover]);

  // Disconnect a connection by provider name
  const disconnectProvider = useCallback(async (provider) => {
    try {
      setError(null);
      const userId = getUserIdDiscover();
      await disconnectConnectionApi(provider, userId);
      
      // Refresh connections after disconnect
      await fetchConnections();
      
      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'Failed to disconnect';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    }
  }, [fetchConnections, getUserIdDiscover]);

  // Refresh connections (manual)
  const refreshConnections = useCallback(() => {
    return fetchConnections();
  }, [fetchConnections]);

  // Check if a provider is connected
  const isProviderConnected = useCallback((provider) => {
    return connections.some(conn => conn.provider?.toLowerCase() === provider?.toLowerCase() && conn.status === 'active');
  }, [connections]);

  // Get connection for a specific provider
  const getProviderConnection = useCallback((provider) => {
    return connections.find(conn => conn.provider?.toLowerCase() === provider?.toLowerCase());
  }, [connections]);

  const value = {
    connections,
    isLoading,
    isConnecting,
    error,
    initiateOAuth,
    disconnectProvider,
    refreshConnections,
    isProviderConnected,
    getProviderConnection,
  };

  return (
    <OAuthContext.Provider value={value}>
      {children}
    </OAuthContext.Provider>
  );
};

OAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};